import type mapboxgl from 'mapbox-gl';
import { useEffect } from 'react';

type ViewportSyncDetail = {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
  sourceId: string;
};

declare global {
  interface Window {
    __GENERIC_MAP_SYNC__?: { disabled: boolean };
  }
}

const isViewportSyncEvent = (
  evt: Event
): evt is CustomEvent<ViewportSyncDetail> =>
  evt instanceof CustomEvent &&
  Array.isArray(evt.detail?.center) &&
  evt.detail.center.length === 2 &&
  evt.detail.center.every(
    (value: unknown): value is number => typeof value === 'number'
  ) &&
  typeof evt.detail.zoom === 'number' &&
  typeof evt.detail.pitch === 'number' &&
  typeof evt.detail.bearing === 'number' &&
  typeof evt.detail.sourceId === 'string';

export function useSyncViewport({
  mapRef,
  mapIdRef,
  isEnabled,
  isReady,
  isApplyingRemoteViewportRef,
}: {
  mapRef: React.MutableRefObject<mapboxgl.Map | null>;
  mapIdRef: React.MutableRefObject<string>;
  isEnabled: boolean;
  isReady: boolean;
  isApplyingRemoteViewportRef: React.MutableRefObject<boolean>;
}) {
  useEffect(() => {
    if (!isEnabled || !isReady) return;
    const map = mapRef.current;
    if (!map) return;

    if (!window.__GENERIC_MAP_SYNC__) {
      window.__GENERIC_MAP_SYNC__ = { disabled: false };
    }

    const handleMove = () => {
      if (isApplyingRemoteViewportRef.current) return;
      const sync = window.__GENERIC_MAP_SYNC__;
      if (sync?.disabled) return;

      const centerPoint = map.getCenter();
      const center: [number, number] = [centerPoint.lng, centerPoint.lat];
      const detail: ViewportSyncDetail = {
        center,
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
        sourceId: mapIdRef.current ?? '',
      };

      window.dispatchEvent(new CustomEvent('generic-map-viewport', { detail }));
    };

    const handleViewportSync = (e: Event) => {
      const currentMap = mapRef.current;
      if (!currentMap) return;
      if (!isViewportSyncEvent(e)) return;
      if (e.detail.sourceId === mapIdRef.current) return;

      const sync = window.__GENERIC_MAP_SYNC__;
      if (!sync || sync.disabled) return;

      sync.disabled = true;
      isApplyingRemoteViewportRef.current = true;
      try {
        currentMap.setCenter(e.detail.center);
        currentMap.setZoom(e.detail.zoom);
        currentMap.setPitch(e.detail.pitch);
        currentMap.setBearing(e.detail.bearing);
      } finally {
        sync.disabled = false;
        isApplyingRemoteViewportRef.current = false;
      }
    };

    map.on('move', handleMove);
    window.addEventListener('generic-map-viewport', handleViewportSync);

    return () => {
      map.off('move', handleMove);
      window.removeEventListener('generic-map-viewport', handleViewportSync);
    };
  }, [isEnabled, isReady, isApplyingRemoteViewportRef, mapIdRef, mapRef]);
}
